import loc, { type LocalizedString, type ResourceId } from 'owa-localize';
import { executePrompt } from './executePrompt';
import { executePromptWithQueryAnnotation } from './executePromptWithQueryAnnotation';
import { getInboxBriefingCommandText, getCMMIDForInboxBriefing } from './inboxBriefingUtils';
import {
    COMPOSE_NEW_HELP_WRITE_CMM_ID,
    COMPOSE_NEW_FIND_RECIPIENTS_CMM_ID,
    COMPOSE_NEW_SUPPORT_STATEMENT_CMM_ID,
    COMPOSE_REPLY_HELP_REPLY_CMM_ID,
    COMPOSE_REPLY_NEXT_STEPS_CMM_ID,
    COMPOSE_REPLY_KEY_POINTS_CMM_ID,
    COMPOSE_FORWARD_EXPLAIN_CMM_ID,
    COMPOSE_FORWARD_WITH_CONTEXT_CMM_ID,
    COMPOSE_FORWARD_WITH_TASKS_CMM_ID,
} from 'owa-bizchat-shared/lib/utils/ScenarioCMMIds';
import {
    composeNewHelpWriteCommandText,
    composeNewHelpWriteDisplayText,
    composeNewFindRecipientsCommandText,
    composeNewFindRecipientsDisplayText,
    composeNewSupportStatementCommandText,
    composeNewSupportStatementDisplayText,
    composeReplyHelpReplyCommandText,
    composeReplyHelpReplyDisplayText,
    composeReplyNextStepsCommandText,
    composeReplyNextStepsDisplayText,
    composeReplyKeyPointsCommandText,
    composeReplyKeyPointsDisplayText,
    composeForwardExplainCommandText,
    composeForwardExplainDisplayText,
    composeForwardWithContextCommandText,
    composeForwardWithContextDisplayText,
    composeForwardWithTasksCommandText,
    composeForwardWithTasksDisplayText,
    showTopActionItemsCommandText,
    showTopActionItemsDisplayText,
    findQuickWinsCommandText,
    findQuickWinsDisplayText,
    getOrientedCommandText,
    getOrientedDisplayText,
    latestOnTopicCommandText,
    latestOnTopicDisplayText,
    needsUrgentRepliesCommandText,
    needsUrgentRepliesDisplayText,
    showKeyTakeawaysCommandText,
    showKeyTakeawaysDisplayText,
    showMyTopThreeEmailsCommandText,
    showMyTopThreeEmailsDisplayText,
    showRelatedResourcesCommandText,
    showRelatedResourcesDisplayText,
    suggestNextStepsCommandText,
    suggestNextStepsDisplayText,
    summarizeAndReplyCommandText,
    summarizeAndReplyDisplayText,
    summarizeCommandText,
    summarizeDisplayText,
    triageMyInboxCommandText,
    triageMyInboxDisplayText,
    whatToGetToFirstCommandText,
    whatToGetToFirstDisplayText,
    inboxBriefingDisplayText,
} from './suggestionCatalog.locstring.json';

type BaseSuggestion = {
    /** Unique identifier */
    id: string;
    /** Callback function to execute when the suggestion is clicked */
    onClick: (targetWindow: Window) => void;
};

// Compose DAB SUAs - New Email
export const composeNewHelpWrite: Suggestion = {
    displayText: composeNewHelpWriteDisplayText,
    id: 'composeNewHelpWrite',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeNewHelpWriteCommandText),
            COMPOSE_NEW_HELP_WRITE_CMM_ID,
            targetWindow
        ),
};

export const composeNewFindRecipients: Suggestion = {
    displayText: composeNewFindRecipientsDisplayText,
    id: 'composeNewFindRecipients',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeNewFindRecipientsCommandText),
            COMPOSE_NEW_FIND_RECIPIENTS_CMM_ID,
            targetWindow
        ),
};

export const composeNewSupportStatement: Suggestion = {
    displayText: composeNewSupportStatementDisplayText,
    id: 'composeNewSupportStatement',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeNewSupportStatementCommandText),
            COMPOSE_NEW_SUPPORT_STATEMENT_CMM_ID,
            targetWindow
        ),
};

// Compose DAB SUAs - Reply
export const composeReplyHelpReply: Suggestion = {
    displayText: composeReplyHelpReplyDisplayText,
    id: 'composeReplyHelpReply',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeReplyHelpReplyCommandText),
            COMPOSE_REPLY_HELP_REPLY_CMM_ID,
            targetWindow
        ),
};

export const composeReplyNextSteps: Suggestion = {
    displayText: composeReplyNextStepsDisplayText,
    id: 'composeReplyNextSteps',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeReplyNextStepsCommandText),
            COMPOSE_REPLY_NEXT_STEPS_CMM_ID,
            targetWindow
        ),
};

export const composeReplyKeyPoints: Suggestion = {
    displayText: composeReplyKeyPointsDisplayText,
    id: 'composeReplyKeyPoints',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeReplyKeyPointsCommandText),
            COMPOSE_REPLY_KEY_POINTS_CMM_ID,
            targetWindow
        ),
};

// Compose DAB SUAs - Forward
export const composeForwardExplain: Suggestion = {
    displayText: composeForwardExplainDisplayText,
    id: 'composeForwardExplain',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeForwardExplainCommandText),
            COMPOSE_FORWARD_EXPLAIN_CMM_ID,
            targetWindow
        ),
};

export const composeForwardWithContext: Suggestion = {
    displayText: composeForwardWithContextDisplayText,
    id: 'composeForwardWithContext',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeForwardWithContextCommandText),
            COMPOSE_FORWARD_WITH_CONTEXT_CMM_ID,
            targetWindow
        ),
};

export const composeForwardWithTasks: Suggestion = {
    displayText: composeForwardWithTasksDisplayText,
    id: 'composeForwardWithTasks',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(composeForwardWithTasksCommandText),
            COMPOSE_FORWARD_WITH_TASKS_CMM_ID,
            targetWindow
        ),
};

// Ensures that one of either `displayText` or `displayTextLocalized` is defined
// TODO: Eventually, using only `displayText: LocalizedString` would be ideal
export type Suggestion =
    | (BaseSuggestion & {
          displayText: ResourceId;
          displayTextLocalized?: never;
      })
    | (BaseSuggestion & {
          displayText?: never;
          displayTextLocalized: LocalizedString | string;
      });

type NonEmptyArray<T> = [T, ...T[]];

export type Suggestions = NonEmptyArray<Suggestion> | undefined;

export const findQuickWins: Suggestion = {
    displayText: findQuickWinsDisplayText,
    id: 'findQuickWins',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(findQuickWinsCommandText), 'cmms7bt06i3', targetWindow),
};

export const getOriented: Suggestion = {
    displayText: getOrientedDisplayText,
    id: 'getOriented',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(getOrientedCommandText), 'cmmmljyih87', targetWindow),
};

export const suggestNextSteps: Suggestion = {
    displayText: suggestNextStepsDisplayText,
    id: 'suggestNextSteps',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(suggestNextStepsCommandText),
            'cmmnga5qm2r',
            targetWindow
        ),
};

export const summarize: Suggestion = {
    displayText: summarizeDisplayText,
    id: 'summarize',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(loc(summarizeCommandText), 'cmmemdb07yk', targetWindow),
};

export const summarizeAndReply: Suggestion = {
    displayText: summarizeAndReplyDisplayText,
    id: 'summarizeAndReply',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(summarizeAndReplyCommandText),
            'cmm9uaofbq8',
            targetWindow
        ),
};

export const triageMyInbox: Suggestion = {
    displayText: triageMyInboxDisplayText,
    id: 'triageMyInbox',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(triageMyInboxCommandText), 'cmm2syst0oj', targetWindow),
};

export const showKeyTakeaways: Suggestion = {
    displayText: showKeyTakeawaysDisplayText,
    id: 'showKeyTakeaways',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(showKeyTakeawaysCommandText),
            'cmmnaejze13',
            targetWindow
        ),
};

export const showRelatedResources: Suggestion = {
    displayText: showRelatedResourcesDisplayText,
    id: 'showRelatedResources',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(showRelatedResourcesCommandText),
            'cmmsh5so9e9',
            targetWindow
        ),
};

export const needsUrgentReplies: Suggestion = {
    displayText: needsUrgentRepliesDisplayText,
    id: 'needsUrgentReplies',
    onClick: (targetWindow: Window) =>
        executePromptWithQueryAnnotation(
            loc(needsUrgentRepliesCommandText),
            'cmmh7ruakpo',
            targetWindow
        ),
};

export const showTopActionItems: Suggestion = {
    displayText: showTopActionItemsDisplayText,
    id: 'showTopActionItems',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(showTopActionItemsCommandText), 'cmmn6jg6pjz', targetWindow),
};

export const latestOnTopic: Suggestion = {
    displayText: latestOnTopicDisplayText,
    id: 'latestOnTopic',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(latestOnTopicCommandText), 'cmmo9ia57a5', targetWindow),
};

export const whatToGetToFirst: Suggestion = {
    displayText: whatToGetToFirstDisplayText,
    id: 'whatToGetToFirst',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(whatToGetToFirstCommandText), 'cmmnr4v9nfp', targetWindow),
};

export const showMyTopThreeEmails: Suggestion = {
    displayText: showMyTopThreeEmailsDisplayText,
    id: 'showMyTopThreeEmails',
    onClick: (targetWindow: Window) =>
        executePrompt(loc(showMyTopThreeEmailsCommandText), 'cmm9byc18nf', targetWindow),
};

export const inboxBriefing: Suggestion = {
    displayText: inboxBriefingDisplayText,
    id: 'inboxBriefing',
    onClick: (targetWindow: Window) =>
        executePrompt(getInboxBriefingCommandText(), getCMMIDForInboxBriefing(), targetWindow),
};
